
const getStatusBadge = (status, serviceType) => {
    const isTransport = serviceType === "passenger" || serviceType === "transport";
    if (status === "delivered" || status === "completed") {
        return {
            label: isTransport ? "KOUS LA FINI" : "LIVREZON FÈT",
            cls: "bg-green-100 text-green-700 border-green-200"
        };
    }
    if (status === "on_the_way") {
        return {
            label: isTransport ? "AN WOUT AK PASAJE" : "AN WOUT",
            cls: "bg-orange-100 text-orange-600 border-orange-200"
        };
    }
    if (status === "arrived_pickup") {
        return {
            label: "RIVE KOTE PASAJE",
            cls: "bg-purple-100 text-purple-600 border-purple-200"
        };
    }
    if (status === "arrived_store") {
        return {
            label: "RIVE MAGAZEN",
            cls: "bg-purple-100 text-purple-600 border-purple-200"
        };
    }
    if (status === "picked_up") {
        return {
            label: isTransport ? "PASAJE A MONTE" : "PRAN KOLI",
            cls: "bg-amber-100 text-amber-600 border-amber-200"
        };
    }
    if (status === "accepted") {
        return {
            label: "ASEPTE",
            cls: "bg-blue-100 text-blue-600 border-blue-200"
        };
    }
    if (status === "pending_driver") {
        return {
            label: "SAN CHOFÈ",
            cls: "bg-red-100 text-red-600 border-red-200"
        };
    }
    if (status === "cancelled") {
        return {
            label: "ANILE",
            cls: "bg-gray-100 text-gray-500 border-gray-200"
        };
    }
    return {
        label: status || "ENKONI",
        cls: "bg-gray-100 text-gray-500 border-gray-200"
    };
};

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
    Truck, Package, User, Phone, MapPin, 
    Navigation, AlertCircle, CheckCircle, 
    Clock, Search, ArrowRight, Loader2,
    Calendar, ShieldCheck, Mail, Plus, X, PhoneCall,
    Map, RefreshCw, CheckCheck, Trash2, Eye
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ===================== STATUS CONFIG =====================
const ENT_DELIVERY_STEPS = [
    { key: "accepted",       label: "Asepte Kous",            color: "text-blue-500",   bg: "bg-blue-50",   border: "border-blue-200" },
    { key: "arrived_store",  label: "Rive Magazen/Kote Depa", color: "text-purple-500", bg: "bg-purple-50", border: "border-purple-200" },
    { key: "picked_up",      label: "Pran Koli / Pasaje",     color: "text-amber-500",  bg: "bg-amber-50",  border: "border-amber-200" },
    { key: "on_the_way",     label: "An Wout",                color: "text-orange-500", bg: "bg-orange-50", border: "border-orange-200" },
    { key: "delivered",      label: "Kous la Fini",           color: "text-green-500",  bg: "bg-green-50",  border: "border-green-200" },
];
const ENT_TRANSPORT_STEPS = [
    { key: "accepted",       label: "Asepte Kous",            color: "text-blue-500",   bg: "bg-blue-50",   border: "border-blue-200" },
    { key: "arrived_pickup", label: "Rive Kote Pasaje a",     color: "text-purple-500", bg: "bg-purple-50", border: "border-purple-200" },
    { key: "on_the_way",     label: "An Wout ak Pasaje",      color: "text-orange-500", bg: "bg-orange-50", border: "border-orange-200" },
    { key: "delivered",      label: "Kous la Fini",           color: "text-green-500",  bg: "bg-green-50",  border: "border-green-200" },
];
const ENT_STATUS_BADGE = {
    pending_driver: { label: "San Chofe",         cls: "bg-red-100 text-red-600 border-red-200" },
    accepted:       { label: "Asepte",            cls: "bg-blue-100 text-blue-600 border-blue-200" },
    arrived_store:  { label: "Rive Magazen",      cls: "bg-purple-100 text-purple-600 border-purple-200" },
    arrived_pickup: { label: "Rive Kote Pasaje",  cls: "bg-purple-100 text-purple-600 border-purple-200" },
    picked_up:      { label: "Pran Koli",         cls: "bg-amber-100 text-amber-600 border-amber-200" },
    on_the_way:     { label: "An Wout",           cls: "bg-orange-100 text-orange-600 border-orange-200" },
    delivered:      { label: "Livrezon Fet",      cls: "bg-green-100 text-green-600 border-green-200" },
    completed:      { label: "Konplete",          cls: "bg-green-100 text-green-600 border-green-200" },
    cancelled:      { label: "Anile",             cls: "bg-gray-100 text-gray-500 border-gray-200" },
};
const entFmtTime = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleTimeString("fr-HT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
    catch { return null; }
};

const EntTrackingPanel = ({ order, onClose }) => {
    const [track, setTrack] = useState(null);
    const [loading, setLoading] = useState(true);
    const orderId = order?.id || order?.order_id;

    const fetchTrack = useCallback(async () => {
        if (!orderId) return;
        try {
            const res = await axios.get(`${API}/drivers/dispatch/track/${orderId}`);
            setTrack(res.data);
        } catch (e) { console.error("Track error:", e); }
        finally { setLoading(false); }
    }, [orderId]);

    useEffect(() => {
        fetchTrack();
        const iv = setInterval(() => {
            setTrack(prev => {
                const st = prev?.status;
                if (!st || ["delivered","completed","cancelled"].includes(st)) return prev;
                fetchTrack();
                return prev;
            });
        }, 5000);
        return () => clearInterval(iv);
    }, [fetchTrack]);

    const steps = (track?.service_type === "passenger" || track?.service_type === "transport")
        ? ENT_TRANSPORT_STEPS : ENT_DELIVERY_STEPS;
    const activeIdx = (() => {
        if (!track) return -1;
        const st = track.status;
        if (["delivered","completed"].includes(st)) return steps.length - 1;
        const i = steps.findIndex(s => s.key === st);
        return i >= 0 ? i : (track?.timeline?.accepted ? 0 : -1);
    })();
    const tlKey = (k, tl) => tl?.[k] || tl?.[k + "_at"] || null;
    const hasGps = Boolean(track?.driver_gps?.lat && track?.driver_gps?.lng);
    const mapsUrl = hasGps ? `https://www.google.com/maps?q=${track.driver_gps.lat},${track.driver_gps.lng}` : null;
    const badge = getStatusBadge(track?.status, track?.service_type);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="bg-[#232F3E] px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Suivi Traje Chofe</p>
                        <h3 className="text-white font-black text-sm italic uppercase truncate">
                            {track?.product_title || order?.product_title || `Kous ${String(orderId || "").slice(0,8)}`}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={fetchTrack} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all" title="Rafrayi"><RefreshCw className="w-3.5 h-3.5" /></button>
                        <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"><X className="w-4 h-4" /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#FF9900] animate-spin" /></div>
                    ) : (<>
                        <div className="flex items-center justify-between">
                            <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${badge.cls}`}>{badge.label}</span>
                            {hasGps && (
                                <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9900] hover:bg-[#e68a00] text-black rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-md">
                                    <Map className="w-3 h-3" /> We sou Kat
                                </a>
                            )}
                        </div>
                        {track?.driver_info?.fullname && (
                            <div className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-[#232F3E] flex items-center justify-center text-white font-black text-base shrink-0 overflow-hidden">
                                    {track.driver_info.profile_photo ? <img src={track.driver_info.profile_photo} alt="" className="w-full h-full object-cover" /> : (track.driver_info.fullname || "C")[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-black text-gray-900 italic uppercase truncate">{track.driver_info.fullname}</p>
                                    <div className="flex items-center gap-3 mt-0.5">
                                        <span className="text-[9px] font-black bg-[#FF9900]/10 text-[#FF9900] px-2 py-0.5 rounded-full uppercase tracking-widest">{track.driver_info.vehicle_type || "Chofe"}</span>
                                        <span className="text-[10px] font-bold text-gray-400">{track.driver_info.phone}</span>
                                    </div>
                                </div>
                                {hasGps && <div className="flex flex-col items-end"><div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /><span className="text-[9px] font-black text-green-500 uppercase">GPS Aktif</span></div>{track.driver_gps.last_seen && <span className="text-[8px] text-gray-400 mt-0.5">{entFmtTime(track.driver_gps.last_seen)}</span>}</div>}
                            </div>
                        )}
                        <div className="space-y-2">
                            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-3">
                                <div className="w-6 h-6 rounded-lg bg-blue-500 flex items-center justify-center shrink-0 mt-0.5"><MapPin className="w-3 h-3 text-white" /></div>
                                <div><p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">{(track?.service_type === "passenger" || track?.service_type === "transport") ? "Kote Pasaje a ye (Depa)" : "Kote Depa / Magazen"}</p><p className="text-xs font-bold text-gray-800">{track?.pickup_address || "—"}</p></div>
                            </div>
                            <div className="flex items-start gap-3 bg-green-50 rounded-xl p-3">
                                <div className="w-6 h-6 rounded-lg bg-green-500 flex items-center justify-center shrink-0 mt-0.5"><CheckCheck className="w-3 h-3 text-white" /></div>
                                <div><p className="text-[8px] font-black text-green-400 uppercase tracking-widest">{(track?.service_type === "passenger" || track?.service_type === "transport") ? "Destinasyon Pasaje a" : "Destinasyon Koli a"}</p><p className="text-xs font-bold text-gray-800">{track?.dropoff_address || "—"}</p></div>
                            </div>
                        </div>
                        <div>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">Pwogre Kous La</p>
                            <div className="space-y-2">
                                {steps.map((step, idx) => {
                                    const done = idx <= activeIdx;
                                    const active = idx === activeIdx;
                                    const ts = entFmtTime(tlKey(step.key, track?.timeline));
                                    return (
                                        <div key={step.key} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${active ? `${step.bg} ${step.border} shadow-sm` : done ? "bg-gray-50 border-gray-100" : "bg-white border-gray-50 opacity-40"}`}>
                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${done ? step.bg : "bg-gray-100"}`}>
                                                {done ? <CheckCircle className={`w-4 h-4 ${step.color}`} /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-[11px] font-black uppercase tracking-wide ${active ? step.color : done ? "text-gray-700" : "text-gray-300"}`}>{step.label}</p>
                                                {ts && <p className="text-[9px] text-gray-400 font-bold">{ts}</p>}
                                            </div>
                                            {active && <div className="w-2 h-2 rounded-full bg-[#FF9900] animate-pulse shrink-0" />}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {(track?.client_name || track?.client_phone) && (
                            <div className="bg-gray-50 rounded-2xl p-4">
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">Enfòmasyon Kliyan</p>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs font-bold text-gray-700">{track.client_name}</span>
                                    {track.client_phone && <><Phone className="w-4 h-4 text-gray-400" /><span className="text-xs font-bold text-gray-700">{track.client_phone}</span></>}
                                </div>
                            </div>
                        )}
                        {/* FOTO PREV */}
                        {(track?.pickup_photo || track?.delivery_photo || track?.house_photo) && (
                            <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">📸 Foto Prèv Chofe a</p>
                                <div className="grid grid-cols-3 gap-2">
                                    {track.pickup_photo && (
                                        <a href={track.pickup_photo} target="_blank" rel="noopener noreferrer" className="relative group block">
                                            <img src={track.pickup_photo} alt="Pickup" className="w-full h-24 object-cover rounded-xl border-2 border-amber-200 group-hover:border-amber-400 transition-all shadow-sm" />
                                            <div className="absolute bottom-1 left-1 right-1 text-center">
                                                <span className="text-[7px] font-black bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase">📦 Magazen</span>
                                            </div>
                                        </a>
                                    )}
                                    {track.delivery_photo && (
                                        <a href={track.delivery_photo} target="_blank" rel="noopener noreferrer" className="relative group block">
                                            <img src={track.delivery_photo} alt="Livrezon" className="w-full h-24 object-cover rounded-xl border-2 border-green-200 group-hover:border-green-400 transition-all shadow-sm" />
                                            <div className="absolute bottom-1 left-1 right-1 text-center">
                                                <span className="text-[7px] font-black bg-green-600 text-white px-1.5 py-0.5 rounded-full uppercase">✅ Koli</span>
                                            </div>
                                        </a>
                                    )}
                                    {track.house_photo && (
                                        <a href={track.house_photo} target="_blank" rel="noopener noreferrer" className="relative group block">
                                            <img src={track.house_photo} alt="Kay" className="w-full h-24 object-cover rounded-xl border-2 border-blue-200 group-hover:border-blue-400 transition-all shadow-sm" />
                                            <div className="absolute bottom-1 left-1 right-1 text-center">
                                                <span className="text-[7px] font-black bg-blue-600 text-white px-1.5 py-0.5 rounded-full uppercase">🏠 Kay</span>
                                            </div>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </>)}
                </div>
            </div>
        </div>
    );
};

export const EnterpriseDispatcher = () => {
    const [orders, setOrders] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(null); // ID of driver being assigned
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showManualModal, setShowManualModal] = useState(false);
    const [manualForm, setManualForm] = useState({
        client_name: "", client_phone: "", pickup_address: "", dropoff_address: "", notes: "", price_htg: ""
    });
    const [creatingOrder, setCreatingOrder] = useState(false);
    const [assignMode, setAssignMode] = useState("open"); // "open" or "assign"
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [hiddenOrderIds, setHiddenOrderIds] = useState(() => {
        try { return JSON.parse(localStorage.getItem("dispatch_hidden_orders") || "[]"); } catch { return []; }
    });
    const [showHistory, setShowHistory] = useState(false);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 10000); // 10s refresh
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            // 1. Fetch Marketplace Orders (We filter for pending_driver in UI)
            const ordersRes = await axios.get(`${API}/admin/marketplace/orders`);
            
            // 2. Fetch Active Drivers
            const driversRes = await axios.get(`${API}/drivers/list-active`);
            
            setOrders(ordersRes.data);
            setDrivers(driversRes.data.drivers || []);
        } catch (error) {
            console.error("Dispatcher fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm("Ou asire ou vle efase kòmand sa nèt? Ou p'ap ka wè l ankò.")) return;
        try {
            const res = await axios.delete(`${API}/admin/marketplace/orders/${orderId}`);
            if (res.data.success) {
                toast.success("Kòmand efase avek siksè!");
                fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Echek lè w t'ap efase kòmand lan.");
        }
    };

    const handleAssign = async (orderId, driverId) => {
        setAssigning(driverId);
        try {
            const res = await axios.post(`${API}/drivers/assign-order/${orderId}`, { driver_id: driverId });
            if (res.data.success) {
                toast.success("✅ Kòmand asiyen avèk siksè!");
                setSelectedOrder(null);
                fetchData();
            }
        } catch (error) {
            console.error("Assignment error:", error);
            toast.error(error.response?.data?.detail || "Echkèk nan asiyasyon an.");
        } finally {
            setAssigning(null);
        }
    };

    const handleCreateManualOrder = async (e) => {
        e.preventDefault();
        if (!manualForm.client_name || !manualForm.client_phone || !manualForm.pickup_address || !manualForm.dropoff_address) {
            toast.error("Tanpri ranpli tout champ obligatwa yo!");
            return;
        }
        if (assignMode === "assign" && !selectedDriverId) {
            toast.error("Tanpri chwazi yon chofè!");
            return;
        }
        setCreatingOrder(true);
        try {
            // Step 1: Create the order
            const res = await axios.post(`${API}/drivers/create-manual-order`, {
                service_type: manualForm.service_type || "passenger",
                client_name: manualForm.client_name,
                client_phone: manualForm.client_phone,
                pickup_address: manualForm.pickup_address,
                dropoff_address: manualForm.dropoff_address,
                notes: manualForm.notes,
                price_htg: parseFloat(manualForm.price_htg) || 0,
                assign_to_driver: assignMode === "assign" ? selectedDriverId : null
            });
            if (res.data.success) {
                // Step 2: If "assign" mode, directly assign the driver
                if (assignMode === "assign" && selectedDriverId && res.data.order_id) {
                    try {
                        await axios.post(`${API}/drivers/assign-order/${res.data.order_id}`, { driver_id: selectedDriverId });
                        const driverName = drivers.find(d => d.id === selectedDriverId)?.fullname || "Chofè";
                        toast.success(`Kòmand kreye epi asiyen bay ${driverName} dirèkteman!`);
                    } catch (assignErr) {
                        toast.success("Kòmand kreye! (Asiyasyon echwe - Chofè ka aksepte li)");
                    }
                } else {
                    toast.success(`Kòmand lage lib! ${res.data.drivers_notified} chofè notifye.`);
                }
                setShowManualModal(false);
                setManualForm({ service_type: "passenger", client_name: "", client_phone: "", pickup_address: "", dropoff_address: "", notes: "", price_htg: "" });
                setAssignMode("open");
                setSelectedDriverId("");
                fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Echèk nan kreyasyon kòmand lan.");
        } finally {
            setCreatingOrder(false);
        }
    };

    const hideOrder = (orderId) => {
        const newHidden = [...hiddenOrderIds, orderId];
        setHiddenOrderIds(newHidden);
        localStorage.setItem("dispatch_hidden_orders", JSON.stringify(newHidden));
        toast.success("Kòmand kache avèk siksè!");
    };

    const restoreOrder = (orderId) => {
        const newHidden = hiddenOrderIds.filter(id => id !== orderId);
        setHiddenOrderIds(newHidden);
        localStorage.setItem("dispatch_hidden_orders", JSON.stringify(newHidden));
        toast.success("Kòmand retounen nan lis la!");
    };

    const clearAllHidden = () => {
        setHiddenOrderIds([]);
        localStorage.removeItem("dispatch_hidden_orders");
        toast.success("Tout kòmand kache yo retire!");
    };

    const activeOrders_ENT = orders.filter(o => {
        const s = o.delivery_info?.status || o.status;
        return Boolean(o.delivery_info?.driver_id) && !["delivered", "completed", "cancelled"].includes(s);
    });

    const pendingOrders = orders.filter(o => {
        if (hiddenOrderIds.includes(o.id || o.order_id)) return false;
        return o.delivery_info?.status === 'pending_driver' || 
            o.status === 'pending_driver' ||
            (o.order_type === 'manual_dispatch' && !o.delivery_info?.driver_id && !['delivered','completed','cancelled'].includes(o.delivery_info?.status || o.status));
    });

    const historyOrders = orders.filter(o => {
        const s = o.delivery_info?.status || o.status || "";
        return ["delivered","completed","cancelled"].includes(s);
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const filteredDrivers = drivers.filter(d => 
        d.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.phone.includes(searchQuery)
    );

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-HT', { style: 'decimal', minimumFractionDigits: 0 }).format(price);
    };

    if (loading && orders.length === 0) {
        return (
            <EnterpriseLayout>
            {trackingOrder && <EntTrackingPanel order={trackingOrder} onClose={() => setTrackingOrder(null)} />}
                <div className="flex flex-col items-center justify-center py-40">
                    <Loader2 className="w-12 h-12 animate-spin text-[#FF9900] mb-4" />
                    <p className="text-gray-400 font-black uppercase tracking-widest text-xs italic">Ap Chaje Dispatch Hub...</p>
                </div>
            </EnterpriseLayout>
        );
    }

    return (
        <EnterpriseLayout>
            {trackingOrder && <EntTrackingPanel order={trackingOrder} onClose={() => setTrackingOrder(null)} />}
            <div className="space-y-8 min-h-screen pb-20">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-4xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Dispatch <span className="text-[#FF9900]">Hub</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2 flex items-center gap-2">
                             Systeme distribisyon vwayaj an tan reyèl <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            id="btn-create-manual-order"
                            onClick={() => setShowManualModal(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-[#232F3E] hover:bg-black text-white font-black rounded-2xl uppercase text-[10px] tracking-widest shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 whitespace-nowrap"
                        >
                            <PhoneCall className="w-4 h-4 text-[#FF9900]" />
                            Kreye Komand
                            <Plus className="w-4 h-4" />
                        </button>
                        <Card className="bg-white px-6 py-4 rounded-[25px] flex items-center gap-4 shadow-xl border-0">
                            <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500">
                                <Clock className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Kòmand nan Atant</p>
                                <p className="text-2xl font-black text-gray-900 leading-none">{pendingOrders.length}</p>
                            </div>
                        </Card>
                        <Card className="bg-white px-6 py-4 rounded-[25px] flex items-center gap-4 shadow-xl border-0">
                            <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center text-green-500">
                                <Truck className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Chofè Disponib</p>
                                <p className="text-2xl font-black text-gray-900 leading-none">{drivers.length}</p>
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
{/* ====== KOMAND FINI AK FOTO ====== */}
                    <div className="lg:col-span-12 bg-white rounded-[30px] border border-gray-100 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-black italic uppercase text-gray-800 flex items-center gap-2">
                                <div className="w-1 h-5 bg-green-500 rounded-full" />
                                Komand Fini ({orders.filter(o => ['delivered','completed'].includes(o.delivery_info?.status || o.status)).length})
                            </h2>
                        </div>
                        {orders.filter(o => ['delivered','completed'].includes(o.delivery_info?.status || o.status)).length === 0 ? (
                            <div className="py-10 text-center">
                                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Pa gen komand fini kounye a.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto custom-scrollbar pr-1">
                                {orders
                                    .filter(o => ['delivered','completed'].includes(o.delivery_info?.status || o.status))
                                    .slice(0, 30)
                                    .map(order => {
                                        const del = order.delivery_info || {};
                                        const badge = getStatusBadge(order.delivery_info?.status || order.status, order.delivery_info?.service_type || order.service_type);
                                        const pickupPhoto = del.pickup_photo || null;
                                        const deliveryPhoto = del.delivery_photo || del.package_photo || null;
                                        const housePhoto = del.house_photo || null;
                                        const hasPhotos = pickupPhoto || deliveryPhoto || housePhoto;
                                        const deliveredAt = del.delivered_at || del.completed_at || order.created_at;
                                        return (
                                            <div key={order.id || order.order_id} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-all">
                                                <div className="p-4 flex items-start gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${badge.cls}`}>{badge.label}</span>
                                                        </div>
                                                        <h4 className="text-sm font-black text-gray-900 italic uppercase truncate">{order.product_title || 'Komand'}</h4>
                                                        <p className="text-[9px] text-gray-400 font-bold mt-0.5">
                                                            {del.driver_name || ''} {del.driver_phone ? ('· ' + del.driver_phone) : ''}
                                                        </p>
                                                        {deliveredAt && (
                                                            <p className="text-[8px] text-gray-300 font-bold">
                                                                {new Date(deliveredAt).toLocaleString('fr-HT', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                                        <p className="text-sm font-black text-gray-800 italic">{(order.price_htg || del.fee || 0).toLocaleString()} HTG</p>
                                                        <div className="flex items-center gap-1.5">
                                                            <button
                                                                onClick={() => setTrackingOrder(order)}
                                                                className="flex items-center gap-1 px-2.5 py-1 bg-[#232F3E] hover:bg-black text-white rounded-lg text-[8px] font-black uppercase tracking-wider transition-all"
                                                            >
                                                                <Eye className="w-3 h-3" /> Foto
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteOrder(order.id || order.order_id)}
                                                                className="flex items-center gap-1 px-2.5 py-1 bg-red-50 hover:bg-red-500 text-red-500 hover:text-white rounded-lg text-[8px] font-black uppercase tracking-wider transition-all border border-red-100"
                                                                title="Efase Komand"
                                                            >
                                                                <Trash2 className="w-3 h-3" /> Fase
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {hasPhotos && (
                                                    <div className="px-4 pb-4">
                                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">Foto Prev</p>
                                                        <div className="flex gap-2">
                                                            {pickupPhoto && (
                                                                <button onClick={() => setTrackingOrder(order)} className="relative group">
                                                                    <img src={pickupPhoto} alt="Pickup" className="w-16 h-16 object-cover rounded-xl border-2 border-amber-200 group-hover:border-amber-400 transition-all" />
                                                                    <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                                                                        <span className="text-[6px] font-black bg-amber-500 text-white px-1 py-0.5 rounded-full uppercase">Magazen</span>
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {deliveryPhoto && (
                                                                <button onClick={() => setTrackingOrder(order)} className="relative group">
                                                                    <img src={deliveryPhoto} alt="Koli" className="w-16 h-16 object-cover rounded-xl border-2 border-green-200 group-hover:border-green-400 transition-all" />
                                                                    <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                                                                        <span className="text-[6px] font-black bg-green-600 text-white px-1 py-0.5 rounded-full uppercase">Koli</span>
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {housePhoto && (
                                                                <button onClick={() => setTrackingOrder(order)} className="relative group">
                                                                    <img src={housePhoto} alt="Kay" className="w-16 h-16 object-cover rounded-xl border-2 border-blue-200 group-hover:border-blue-400 transition-all" />
                                                                    <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                                                                        <span className="text-[6px] font-black bg-blue-600 text-white px-1 py-0.5 rounded-full uppercase">Kay</span>
                                                                    </div>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        )}
                    </div>

                    
                    {/* LEFT: PENDING ORDERS */}
                    <div className="lg:col-span-4 space-y-6">
                        {/* === KOUS AKTIF AN SUIVI === */}
                        {activeOrders_ENT.length > 0 && (
                            <div className="bg-white rounded-[30px] shadow-xl border-2 border-blue-100 p-5 space-y-3 mb-6">
                                <p className="text-[9px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />
                                    Kous Aktif — An Suivi ({activeOrders_ENT.length})
                                </p>
                                <div className="space-y-2 max-h-52 overflow-y-auto custom-scrollbar pr-1">
                                    {activeOrders_ENT.map(order => {
                                        const ordId = order.id || order.order_id || "";
                                        const st = order.delivery_info?.status || order.status || "accepted";
                                        const b = getStatusBadge(st, order.delivery_info?.service_type || order.service_type);
                                        const dName = drivers.find(d => String(d.id) === String(order.delivery_info?.driver_id))?.fullname || order.delivery_info?.driver_name || "—";
                                        return (
                                            <div key={ordId} onClick={() => setTrackingOrder(order)}
                                                className="bg-gray-50 hover:bg-blue-50/50 rounded-2xl border border-blue-100 p-3.5 cursor-pointer hover:shadow-md transition-all group flex items-center justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[11px] font-black text-gray-800 italic truncate">{order.product_title || `Kous ${ordId.slice(0,8)}`}</p>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <div className="w-4 h-4 rounded-lg bg-[#232F3E] flex items-center justify-center text-white text-[7px] font-black shrink-0">{(dName || "C")[0]}</div>
                                                        <span className="text-[9px] font-bold text-gray-500 truncate">{dName}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase border ${b.cls}`}>{b.label}</span>
                                                    <button className="flex items-center gap-1 text-[9px] font-black text-blue-500 bg-blue-100/60 px-2.5 py-1 rounded-xl group-hover:bg-blue-500 group-hover:text-white transition-all">
                                                        <Map className="w-3 h-3" /> Suiv
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        
                        <div className="flex items-center justify-between px-2">
                            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-gray-400 italic">Kòmand ki bezwen Chofè</h2>
                            <span className="bg-[#232F3E] text-white text-[10px] font-black px-3 py-1 rounded-full">{pendingOrders.length}</span>
                        </div>

                        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                            {pendingOrders.length === 0 ? (
                                <div className="bg-white/50 border-2 border-dashed border-gray-100 rounded-[30px] p-12 text-center">
                                    <Package className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest italic">Pa gen kòmand nan atant kounye a</p>
                                </div>
                            ) : (
                                pendingOrders.map(order => (
                                    <div 
                                        key={order.id}
                                        onClick={() => setSelectedOrder(order)}
                                        className={`group relative bg-white p-6 rounded-[30px] border-2 transition-all cursor-pointer hover:shadow-2xl ${selectedOrder?.id === order.id ? 'border-[#FF9900] shadow-2xl scale-[1.02]' : 'border-transparent shadow-xl shadow-gray-100/50'}`}
                                    >
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-[#232F3E] group-hover:bg-[#FF9900]/10 group-hover:text-[#FF9900] transition-colors">
                                                <Package className="w-6 h-6" />
                                            </div>
                                            <div className="text-right">
                                                <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest block mb-1">ID: {order.id.slice(0,8)}</span>
                                                <span className="text-xs font-black text-[#FF9900] tracking-tighter">{formatPrice(order.price_htg)} HTG</span>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Pwodwi</p>
                                                <h4 className="text-sm font-black text-[#232F3E] leading-tight line-clamp-1 italic">{order.product_title}</h4>
                                            </div>

                                            <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
                                                <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
                                                    <MapPin className="w-3.5 h-3.5" />
                                                </div>
                                                <p className="text-[10px] font-bold text-gray-500 truncate italic">{order.delivery_info?.address}</p>
                                            </div>
                                        </div>

                                        {selectedOrder?.id === order.id && (
                                            <div className="absolute -right-2 top-11 bg-[#FF9900] text-black w-8 h-8 rounded-full flex items-center justify-center shadow-lg animate-in fade-in slide-in-from-left-2">
                                                <ArrowRight className="w-5 h-5" />
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* RIGHT: DRIVER ASSIGNMENT */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 px-2">
                            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-gray-400 italic">
                                {selectedOrder ? `Asiyen yon chofè pou [${selectedOrder.id.slice(0,8)}]` : 'Chofè ki An Liy Kounye a'}
                            </h2>
                            <div className="relative w-full md:w-72">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input 
                                    type="text"
                                    placeholder="Chache chofè (Non, Telefòn)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full h-10 pl-11 pr-4 bg-white border border-gray-100 rounded-full text-xs font-bold focus:ring-2 focus:ring-[#FF9900] outline-none transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredDrivers.length === 0 ? (
                                <div className="col-span-full py-32 text-center bg-white rounded-[40px] shadow-xl border border-gray-50 flex flex-col items-center">
                                    <Truck className="w-16 h-16 text-gray-100 mb-6 italic" />
                                    <p className="text-[11px] font-black text-gray-300 uppercase tracking-[0.3em] italic">Pa gen chofè ki disponib kounye a</p>
                                </div>
                            ) : (
                                filteredDrivers.map(driver => {
                                    const driverActive = activeOrders_ENT.find(o => String(o.delivery_info?.driver_id) === String(driver.id));
                                    return (
                                    <Card key={driver.id} className={`bg-white rounded-[35px] border-0 shadow-lg hover:shadow-2xl transition-all overflow-hidden group ${!selectedOrder ? 'opacity-90 hover:opacity-100' : ''}`}>
                                        <div className="p-6">
                                            <div className="flex items-center gap-5 mb-6">
                                                <div className="relative">
                                                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gray-100 border border-gray-50">
                                                        {driver.profile_photo ? (
                                                            <img src={driver.profile_photo} alt={driver.fullname} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center bg-[#232F3E] text-white font-black text-xl italic">
                                                                {driver.fullname[0]}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-base font-black text-gray-900 italic truncate uppercase">{driver.fullname}</h3>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[9px] font-black bg-[#FF9900]/10 text-[#FF9900] px-2 py-0.5 rounded-full uppercase tracking-widest">{driver.vehicle_type}</span>
                                                        <span className="text-[10px] font-bold text-gray-400">{driver.phone}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3 mb-6">
                                                <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-blue-500 shadow-sm">
                                                        <Navigation className="w-4 h-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                         <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Estati</p>
                                                         <p className="text-[10px] font-black text-gray-900 italic truncate">Konekte</p>
                                                    </div>
                                                </div>
                                                <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-amber-500 shadow-sm">
                                                        <Truck className="w-4 h-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                         <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Plate No</p>
                                                         <p className="text-[10px] font-black text-gray-900 italic truncate text-uppercase">{driver.vehicle_details?.slice(0,8) || '—'}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {driverActive && (
                                                <Button 
                                                    onClick={() => setTrackingOrder(driverActive)}
                                                    className="w-full h-10 bg-blue-500 hover:bg-blue-600 text-white font-black rounded-2xl uppercase text-[10px] tracking-widest shadow transition-all flex items-center justify-center gap-2 mb-2"
                                                >
                                                    <Map className="w-4 h-4" /> Suiv Trajè Chofè a
                                                </Button>
                                            )}

                                            {selectedOrder && (
                                                <Button 
                                                    onClick={() => handleAssign(selectedOrder.id, driver.id)}
                                                    disabled={assigning === driver.id}
                                                    className="w-full h-12 bg-[#232F3E] hover:bg-black text-white font-black rounded-2xl uppercase text-[10px] tracking-widest shadow-xl transition-all flex items-center justify-center gap-2"
                                                >
                                                    {assigning === driver.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>Asiyen Chofè <ArrowRight className="w-4 h-4" /></>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    </Card>
                                );})
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* ======= MODAL: KREYE KÒMAND MANYÈL ======= */}
            {showManualModal && (
                <div
                    id="manual-order-modal-overlay"
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowManualModal(false); }}
                >
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="bg-[#232F3E] px-8 py-6 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#FF9900] rounded-2xl flex items-center justify-center">
                                    <PhoneCall className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-white font-black uppercase tracking-widest text-sm italic">Kreye Kòmand</h2>
                                    <p className="text-gray-400 text-[9px] font-bold uppercase tracking-widest">Kliyan ki rele nan telefòn</p>
                                </div>
                            </div>
                            <button
                                id="btn-close-manual-modal"
                                onClick={() => setShowManualModal(false)}
                                className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all"
                            >
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleCreateManualOrder} className="p-8 space-y-5">
                            {/* Service Type Selector (Pasaje vs Koli) */}
                            <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-gray-500 block">
                                    Tip Sèvis la *
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setManualForm(f => ({ ...f, service_type: "passenger" }))}
                                        className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border-2 font-black text-xs uppercase tracking-wider transition-all duration-200 ${
                                            manualForm.service_type === "passenger"
                                            ? "border-cyan-500 bg-cyan-50 text-cyan-950 shadow-md shadow-cyan-500/10"
                                            : "border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200"
                                        }`}
                                    >
                                        <span className="text-base">🚖</span>
                                        <span>Kous Pasaje</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setManualForm(f => ({ ...f, service_type: "delivery" }))}
                                        className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border-2 font-black text-xs uppercase tracking-wider transition-all duration-200 ${
                                            manualForm.service_type === "delivery"
                                            ? "border-[#FF9900] bg-amber-50 text-amber-950 shadow-md shadow-[#FF9900]/10"
                                            : "border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200"
                                        }`}
                                    >
                                        <span className="text-base">📦</span>
                                        <span>Livrezon Koli</span>
                                    </button>
                                </div>
                            </div>

                            {/* Client Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-1">
                                        <User className="w-3 h-3" /> Non Kliyan *
                                    </label>
                                    <input
                                        id="input-client-name"
                                        type="text"
                                        required
                                        placeholder="Ex: Jean Paul"
                                        value={manualForm.client_name}
                                        onChange={e => setManualForm(f => ({...f, client_name: e.target.value}))}
                                        className="w-full h-11 px-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#FF9900] outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-1">
                                        <Phone className="w-3 h-3" /> Telefòn Kliyan *
                                    </label>
                                    <input
                                        id="input-client-phone"
                                        type="tel"
                                        required
                                        placeholder="Ex: 509-3xxx-xxxx"
                                        value={manualForm.client_phone}
                                        onChange={e => setManualForm(f => ({...f, client_phone: e.target.value}))}
                                        className="w-full h-11 px-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#FF9900] outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Pickup Address */}
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-green-500" /> Kote Pran Kliyan (Pickup) *
                                </label>
                                <div className="relative">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-2 h-2 bg-green-500 rounded-full" />
                                    <input
                                        id="input-pickup-address"
                                        type="text"
                                        required
                                        placeholder="Ex: Pétion-Ville, Rte Frères..."
                                        value={manualForm.pickup_address}
                                        onChange={e => setManualForm(f => ({...f, pickup_address: e.target.value}))}
                                        className="w-full h-11 pl-9 pr-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#FF9900] outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Dropoff Address */}
                            <div className="space-y-1.5">
                                <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-red-500" /> Kote Depoze Kliyan (Dropoff) *
                                </label>
                                <div className="relative">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" />
                                    <input
                                        id="input-dropoff-address"
                                        type="text"
                                        required
                                        placeholder="Ex: Port-au-Prince, Champs de Mars..."
                                        value={manualForm.dropoff_address}
                                        onChange={e => setManualForm(f => ({...f, dropoff_address: e.target.value}))}
                                        className="w-full h-11 pl-9 pr-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#FF9900] outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Route Visual */}
                            <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3 border border-dashed border-gray-200">
                                <div className="flex flex-col items-center gap-1">
                                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full" />
                                    <div className="w-0.5 h-6 bg-gray-300" />
                                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                                </div>
                                <div className="flex-1 min-w-0 space-y-2">
                                    <p className="text-[10px] font-black text-gray-700 truncate">{manualForm.pickup_address || "Kote depa..."}</p>
                                    <p className="text-[10px] font-black text-gray-700 truncate">{manualForm.dropoff_address || "Destinasyon..."}</p>
                                </div>
                            </div>

                            {/* Price and Notes */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                                        Pri (HTG) - Opsyonèl
                                    </label>
                                    <input
                                        id="input-price"
                                        type="number"
                                        placeholder="0"
                                        value={manualForm.price_htg}
                                        onChange={e => setManualForm(f => ({...f, price_htg: e.target.value}))}
                                        className="w-full h-11 px-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#FF9900] outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                                        Nòt - Opsyonèl
                                    </label>
                                    <input
                                        id="input-notes"
                                        type="text"
                                        placeholder="Nòt pou chofè a..."
                                        value={manualForm.notes}
                                        onChange={e => setManualForm(f => ({...f, notes: e.target.value}))}
                                        className="w-full h-11 px-4 bg-gray-50 border border-gray-100 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#FF9900] outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* ── DISPATCH MODE SELECTOR ── */}
                            <div className="space-y-3 pt-2 border-t border-gray-100">
                                <label className="text-[9px] font-black uppercase tracking-widest text-gray-500 block">
                                    Opsyon Voye Kous la *
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Option 1: Open - broadcast to all */}
                                    <button
                                        type="button"
                                        id="btn-mode-open"
                                        onClick={() => { setAssignMode("open"); setSelectedDriverId(""); }}
                                        className={`relative flex flex-col items-center gap-2 p-3.5 rounded-2xl border-2 transition-all duration-200 ${
                                            assignMode === "open"
                                                ? "border-[#FF9900] bg-[#FF9900]/5 text-[#232F3E]"
                                                : "border-gray-100 bg-gray-50 hover:border-gray-200 text-gray-400"
                                        }`}
                                    >
                                        {assignMode === "open" && (
                                            <div className="absolute top-2 right-2 w-4 h-4 bg-[#FF9900] rounded-full flex items-center justify-center">
                                                <CheckCircle className="w-3 h-3 text-white" />
                                            </div>
                                        )}
                                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${assignMode === "open" ? "bg-[#FF9900]/15" : "bg-gray-200"}`}>
                                            <Navigation className={`w-4 h-4 ${assignMode === "open" ? "text-[#FF9900]" : "text-gray-400"}`} />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-[10px] font-black uppercase tracking-widest">Lage Lib</p>
                                            <p className="text-[8px] font-bold text-gray-400 mt-0.5">Tout chofè ka aksepte l</p>
                                        </div>
                                    </button>

                                    {/* Option 2: Assign specific driver */}
                                    <button
                                        type="button"
                                        id="btn-mode-assign"
                                        onClick={() => setAssignMode("assign")}
                                        className={`relative flex flex-col items-center gap-2 p-3.5 rounded-2xl border-2 transition-all duration-200 ${
                                            assignMode === "assign"
                                                ? "border-[#232F3E] bg-[#232F3E]/5 text-[#232F3E]"
                                                : "border-gray-100 bg-gray-50 hover:border-gray-200 text-gray-400"
                                        }`}
                                    >
                                        {assignMode === "assign" && (
                                            <div className="absolute top-2 right-2 w-4 h-4 bg-[#232F3E] rounded-full flex items-center justify-center">
                                                <CheckCircle className="w-3 h-3 text-white" />
                                            </div>
                                        )}
                                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${assignMode === "assign" ? "bg-[#232F3E]/10" : "bg-gray-200"}`}>
                                            <User className={`w-4 h-4 ${assignMode === "assign" ? "text-[#232F3E]" : "text-gray-400"}`} />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-[10px] font-black uppercase tracking-widest">Chwazi Chofè</p>
                                            <p className="text-[8px] font-bold text-gray-400 mt-0.5">Asiyen yon chofè an liy</p>
                                        </div>
                                    </button>
                                </div>

                                {/* Driver selector - shown only in assign mode */}
                                {assignMode === "assign" && (
                                    <div className="space-y-1.5 pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                                        <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-1">
                                            <Truck className="w-3 h-3 text-[#232F3E]" /> Chwazi Chofè ki An Liy *
                                        </label>
                                        {drivers.length === 0 ? (
                                            <div className="h-11 px-4 bg-red-50 border border-red-100 rounded-2xl flex items-center">
                                                <p className="text-[10px] font-black text-red-500">Pa gen okenn chofè an liy kounye a</p>
                                            </div>
                                        ) : (
                                            <select
                                                id="select-driver"
                                                value={selectedDriverId}
                                                onChange={e => setSelectedDriverId(e.target.value)}
                                                className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-bold text-gray-800 focus:ring-2 focus:ring-[#232F3E] outline-none transition-all cursor-pointer"
                                            >
                                                <option value="">-- Chwazi yon chofè --</option>
                                                {drivers.map(d => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.fullname} • {d.vehicle_type} ({d.phone})
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                id="btn-submit-manual-order"
                                type="submit"
                                disabled={creatingOrder}
                                className={`w-full h-14 text-white font-black rounded-2xl uppercase text-[10px] tracking-widest shadow-xl transition-all flex items-center justify-center gap-3 disabled:opacity-60 ${
                                    assignMode === "assign" ? "bg-[#232F3E] hover:bg-black" : "bg-[#FF9900] hover:bg-[#e68a00]"
                                }`}
                            >
                                {creatingOrder ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Ap kreye kòmand...</>
                                ) : assignMode === "assign" ? (
                                    <><User className="w-4 h-4" /> Asiyen Dirèkteman Ak Kous La<ArrowRight className="w-4 h-4" /></>
                                ) : (
                                    <><Navigation className="w-4 h-4" /> Voye Kous La Bay Tout Chofè Yo<ArrowRight className="w-4 h-4" /></>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom scrollbar styles */}
            <style dangerouslySetInnerHTML={{ __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
            `}} />
        </EnterpriseLayout>
    );
};







